import React from 'react';
import { Typography, Box, Card, CardContent, Grid, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { statisticsService } from '../services/statisticsService';
import { TimelineChart } from '../components/Charts/TimelineChart';
import { CountryChart } from '../components/Charts/CountryChart';
import { AsnChart } from '../components/Charts/AsnChart';
import { EventMap } from '../components/Charts/EventMap';
import { RecentActivity } from '../components/Dashboard/RecentActivity';
import { EventRateChart } from '../components/EventRateChart';
import { ApiKeyStatsChart } from '../components/ApiKeyStatsChart';
import { BlacklistHourlyChart } from '../components/BlacklistHourlyChart';
import { StatCard } from '../components/Dashboard/StatCard';
import EventIcon from '@mui/icons-material/Event';
import TodayIcon from '@mui/icons-material/Today';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import RouterIcon from '@mui/icons-material/Router';

const Section: React.FC<{ children: React.ReactNode; title: string }> = ({ children, title }) => (
  <Card>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

export const Dashboard: React.FC = () => {
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['statistics', 'summary'],
    queryFn: statisticsService.getSummary,
    refetchInterval: 30000,
  });

  const { data: timeline, isLoading: timelineLoading } = useQuery({
    queryKey: ['statistics', 'timeline'],
    queryFn: () => statisticsService.getTimeline(undefined, undefined, 'hour'),
    refetchInterval: 60000,
  });

  const { data: countries, isLoading: countriesLoading } = useQuery({
    queryKey: ['statistics', 'countries'],
    queryFn: () => statisticsService.getTopCountries(10),
    refetchInterval: 60000,
  });

  const { data: asns, isLoading: asnsLoading } = useQuery({
    queryKey: ['statistics', 'asns'],
    queryFn: () => statisticsService.getTopAsns(10),
    refetchInterval: 60000,
  });

  const { data: geoData, isLoading: geoLoading } = useQuery({
    queryKey: ['statistics', 'geojson'],
    queryFn: () => statisticsService.getGeoJson(),
    refetchInterval: 60000,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Honeypot network activity and threats
        </Typography>
      </Box>

      {/* Error */}
      {summaryError && (
        <Alert severity="error">
          Failed to load dashboard data. Error: {(summaryError as Error).message}
        </Alert>
      )}

      {/* Stat Cards */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Total Events" value={summary?.totalEvents || 0} icon={<EventIcon />} color="primary" loading={summaryLoading} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Last 24h" value={summary?.eventsLast24h || 0} icon={<TodayIcon />} color="info" loading={summaryLoading} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Blacklist" value={summary?.blacklistSize || 0} icon={<BlockIcon />} color="error" loading={summaryLoading} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Whitelist" value={summary?.whitelistSize || 0} icon={<CheckCircleIcon />} color="success" loading={summaryLoading} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Countries" value={summary?.uniqueCountries || 0} icon={<PublicIcon />} color="warning" loading={summaryLoading} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="ASNs" value={summary?.uniqueAsns || 0} icon={<RouterIcon />} color="secondary" loading={summaryLoading} />
        </Grid>
      </Grid>

      {/* Event Rate */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <EventRateChart />
        </CardContent>
      </Card>

      {/* Blacklist Statistics */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <BlacklistHourlyChart />
        </CardContent>
      </Card>

      {/* API Key Stats */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <ApiKeyStatsChart />
        </CardContent>
      </Card>

      {/* Timeline */}
      <Section title="Event Timeline (7 Days)">
        <TimelineChart data={timeline || []} loading={timelineLoading} />
      </Section>

      {/* Countries + ASNs */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Section title="Top Countries">
            <CountryChart data={countries || []} loading={countriesLoading} />
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Top ASNs">
            <AsnChart data={asns || []} loading={asnsLoading} />
          </Section>
        </Grid>
      </Grid>

      {/* Map */}
      <Section title="Geographic Distribution">
        <EventMap data={geoData} loading={geoLoading} />
      </Section>

      {/* Recent Activity */}
      <Section title="Recent Activity">
        <RecentActivity />
      </Section>
    </Box>
  );
};
